import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePolygonEditor } from "../usePolygonEditor";
import {
  FLOOR_PLAN_ROOM_ACTIVE_RGB,
  FLOOR_PLAN_ROOM_RGB,
} from "../../utils/rooms";
import type { PolygonOverlayEntry } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/SvgOverlay/PolygonsLayer/types";
import type {
  PersistPolygonGeometryArgs,
  SavedPolygon,
} from "../usePolygonEditor";
import type { PolygonPoint } from "../../utils/polygonPointMutators";
import type {
  RoomInspectorState,
  SaveRoomArgs,
  UseRoomInspectorArgs,
  UseRoomInspectorResult,
} from "./types";

const NO_POLYGONS: SavedPolygon[] = [];

// The inspector never draws, so the editor's step is fixed and never set.
const keepListStep = () => {};

// The points as moved so far, keyed to their room so another room never shows them.
interface ShapeEdit {
  roomId: number;
  points: PolygonPoint[];
}

export const useRoomInspector = ({
  room,
  pageRooms,
  renameRoom,
  updateRoomGeometry,
  deleteRoom,
  stop,
}: UseRoomInspectorArgs): UseRoomInspectorResult => {
  const { t } = useTranslation();

  const [shapeEdit, setShapeEdit] = useState<ShapeEdit | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const roomId = room?.id;
  const shapePoints =
    shapeEdit !== null && shapeEdit.roomId === roomId ? shapeEdit.points : null;

  const persistGeometry = useCallback(
    ({ id, points }: PersistPolygonGeometryArgs) => {
      setShapeEdit({ roomId: id, points });
      return Promise.resolve();
    },
    [],
  );

  const shapePolygons = useMemo<SavedPolygon[]>(
    () =>
      room !== null
        ? [{ id: room.id, points: shapePoints ?? room.points }]
        : NO_POLYGONS,
    [room, shapePoints],
  );

  const {
    selectedPointId,
    hoverPosition,
    resolvePoints,
    handleMapMouseMove,
    handleNodeMouseDown,
    handleSegmentMouseDown,
  } = usePolygonEditor({
    isActive: room !== null,
    step: "list",
    savedPolygons: shapePolygons,
    setStep: keepListStep,
    stop,
    persistGeometry,
    maxPointsMessage: t("smartSurveyor.rooms.maxPointsReached"),
    minPointsMessage: t("smartSurveyor.rooms.minPointsRequired"),
    fixedTargetId: roomId,
  });

  const isShapeChanged = shapePoints !== null && shapePoints !== room?.points;

  const save = useCallback(
    async ({ name }: SaveRoomArgs) => {
      if (room === null) return;
      if (name !== room.name) renameRoom({ roomId: room.id, name });
      if (isShapeChanged && shapePoints !== null) {
        setIsSaving(true);
        const isSaved = await updateRoomGeometry({
          roomId: room.id,
          points: shapePoints,
        });
        setIsSaving(false);
        if (!isSaved) return;
      }
      stop();
    },
    [room, renameRoom, isShapeChanged, shapePoints, updateRoomGeometry, stop],
  );

  const confirmDelete = useCallback(async () => {
    if (roomId === undefined) return false;
    const isDeleted = await deleteRoom(roomId);
    if (isDeleted) stop();
    return isDeleted;
  }, [roomId, deleteRoom, stop]);

  // Kept apart from the hover-driven overlay, so the other rooms keep their identity and skip re-rendering.
  const lockedEntries = useMemo<PolygonOverlayEntry[]>(
    () =>
      pageRooms
        .filter((candidate) => candidate.id !== roomId)
        .map((candidate) => ({
          id: String(candidate.id),
          points: candidate.points,
          rgb: FLOOR_PLAN_ROOM_RGB,
          label: candidate.name,
          isSelected: false,
          isDraft: false,
          isLocked: true,
        })),
    [pageRooms, roomId],
  );

  const overlay = useMemo(() => {
    const polygons =
      room === null
        ? lockedEntries
        : [
            ...lockedEntries,
            {
              id: String(room.id),
              points: resolvePoints(shapePolygons[0]),
              rgb: FLOOR_PLAN_ROOM_ACTIVE_RGB,
              label: room.name,
              isSelected: true,
              isDraft: false,
            },
          ];

    return {
      polygons,
      hoverPosition,
      isDrawing: false,
      isInteractive: false,
      isEditable: room !== null,
      selectedPointId,
      onNodeMouseDown: handleNodeMouseDown,
      onSegmentMouseDown: handleSegmentMouseDown,
    };
  }, [
    lockedEntries,
    room,
    resolvePoints,
    shapePolygons,
    hoverPosition,
    selectedPointId,
    handleNodeMouseDown,
    handleSegmentMouseDown,
  ]);

  // Any other room, or none, drops the moved points, so a room always opens from what is stored.
  useEffect(() => {
    setShapeEdit(null);
  }, [roomId]);

  const state: RoomInspectorState | null =
    room === null
      ? null
      : { room, isShapeChanged, isSaving, save, cancel: stop, confirmDelete };

  return { state, overlay, handleMapMouseMove };
};
